// ============================================================================
// Adopt & Sign modal — opens on the FIRST click of a signature or initials
// field, which is the industry-standard pattern (DocuSign, Adobe, Dropbox
// Sign, Documenso all do this) and the direct fix for "I clicked the field
// and nothing happened".
//
// Name verification lives here, in context, rather than as a block below the
// document. Once adopted, the signature is reused for every remaining
// signature/initials field for this signer.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { Check, PenLine, Type as TypeIcon, Upload, X } from 'lucide-react';
import { overlayClickClose } from '../../shared/overlay-close';

export type AdoptionMethod = 'typed' | 'drawn' | 'uploaded';

export interface AdoptedSignature {
  /** Rendered signature: plain text (typed) or a PNG data URL (drawn/uploaded). */
  signature: string;
  /** Rendered initials: plain text or a PNG data URL. */
  initials: string;
  name: string;
  method: AdoptionMethod;
  /** Font class used for a typed signature, so the stamp matches the preview. */
  typeface: TypefaceKey;
}

export type TypefaceKey = 'script' | 'serif' | 'mono';

export const TYPEFACES: Record<TypefaceKey, { label: string; className: string }> = {
  script: { label: 'Flowing', className: 'font-serif italic' },
  serif: { label: 'Formal', className: 'font-serif' },
  mono: { label: 'Plain', className: 'font-mono' },
};

export function deriveInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

// ── draw pad ────────────────────────────────────────────────────────────────

function DrawPad({
  onChange, ariaLabel,
}: {
  onChange: (dataUrl: string | null) => void;
  ariaLabel: string;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    c.width = c.offsetWidth * dpr;
    c.height = c.offsetHeight * dpr;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1f2430';
    }
  }, []);

  function pos(e: React.PointerEvent): [number, number] {
    const rect = ref.current!.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  }

  function clear() {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange(null);
  }

  return (
    <div>
      {/* Sized for finger input on a 390px viewport. */}
      <canvas
        ref={ref}
        aria-label={ariaLabel}
        className="h-40 w-full touch-none rounded-md border-2 border-dashed border-tcs-border bg-tcs-raised"
        onPointerDown={(e) => {
          drawing.current = true;
          ref.current?.setPointerCapture(e.pointerId);
          const ctx = ref.current?.getContext('2d');
          const [x, y] = pos(e);
          ctx?.beginPath();
          ctx?.moveTo(x, y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = ref.current?.getContext('2d');
          const [x, y] = pos(e);
          ctx?.lineTo(x, y);
          ctx?.stroke();
          dirty.current = true;
        }}
        onPointerUp={() => {
          drawing.current = false;
          if (dirty.current && ref.current) {
            onChange(ref.current.toDataURL('image/png'));
          }
        }}
      />
      <p className="mt-1 flex items-center justify-between text-xs text-tcs-text-muted">
        <span>Draw with your finger or mouse.</span>
        <button
          type="button"
          onClick={clear}
          className="min-h-[44px] px-2 underline"
        >
          Clear
        </button>
      </p>
    </div>
  );
}

// ── modal ───────────────────────────────────────────────────────────────────

export interface AdoptModalProps {
  /** Which field kind triggered the modal — changes the copy only. */
  trigger: 'signature' | 'initials';
  initialName: string;
  existing: AdoptedSignature | null;
  onAdopt: (a: AdoptedSignature) => void;
  onClose: () => void;
}

export function AdoptModal({
  trigger, initialName, existing, onAdopt, onClose,
}: AdoptModalProps) {
  const [tab, setTab] = useState<AdoptionMethod>(existing?.method ?? 'typed');
  const [name, setName] = useState(existing?.name ?? initialName);
  const [typeface, setTypeface] = useState<TypefaceKey>(existing?.typeface ?? 'script');
  const [drawnSig, setDrawnSig] = useState<string | null>(null);
  const [drawnInitials, setDrawnInitials] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [initialsText, setInitialsText] = useState(
    existing && existing.method === 'typed' ? existing.initials : deriveInitials(initialName),
  );
  const [initialsEdited, setInitialsEdited] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  // Focus moves into the modal on open; Escape closes; focus is trapped.
  useEffect(() => {
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = panelRef.current;
      if (!root) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), canvas, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Initials track the name until the signer edits them by hand.
  useEffect(() => {
    if (!initialsEdited) setInitialsText(deriveInitials(name));
  }, [name, initialsEdited]);

  function handleUpload(file: File) {
    setUploadError(null);
    if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type)) {
      setUploadError('Choose a PNG, JPG, GIF or WEBP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('That image is larger than 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setUploaded(String(reader.result));
    reader.onerror = () => setUploadError('That image could not be read.');
    reader.readAsDataURL(file);
  }

  const nameOk = name.trim().length >= 2;
  const ready =
    nameOk &&
    (tab === 'typed'
      ? initialsText.trim().length > 0
      : tab === 'drawn'
        ? !!drawnSig
        : !!uploaded);

  function adopt() {
    if (!ready) return;
    if (tab === 'typed') {
      onAdopt({
        signature: name.trim(),
        initials: initialsText.trim(),
        name: name.trim(),
        method: 'typed',
        typeface,
      });
      return;
    }
    const img = tab === 'drawn' ? drawnSig! : uploaded!;
    onAdopt({
      signature: img,
      // A drawn initials pad is optional — fall back to the signature image.
      initials: tab === 'drawn' ? (drawnInitials ?? img) : img,
      name: name.trim(),
      method: tab,
      typeface,
    });
  }

  const TabButton = ({
    id, label, icon: Icon,
  }: {
    id: AdoptionMethod;
    label: string;
    icon: typeof TypeIcon;
  }) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      className={`flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-md border px-3 text-sm ${
        tab === id
          ? 'border-tcs-accent-ink bg-[#F4F7FB] font-semibold text-tcs-accent-ink'
          : 'border-tcs-border text-tcs-text'
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="adopt-title"
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 sm:max-w-lg sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="adopt-title" className="text-lg font-semibold text-tcs-text">
              Adopt your signature
            </h2>
            <p className="mt-1 text-sm text-tcs-text-muted">
              Check your name is right, then choose how your{' '}
              {trigger === 'initials' ? 'initials' : 'signature'} should look. We
              reuse it for every field in this document.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-tcs-text-muted"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <label htmlFor="adopt-name" className="mt-4 block text-sm font-medium text-tcs-text">
          Full legal name
        </label>
        <input
          id="adopt-name"
          ref={firstFieldRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          className="mt-1 min-h-[48px] w-full rounded-md border border-tcs-border px-3 text-base"
        />
        {!nameOk && (
          <p className="mt-1 text-xs text-tcs-text-muted">
            Enter your name as you want it to appear on the document.
          </p>
        )}

        <div role="tablist" aria-label="Signature style" className="mt-4 flex gap-2">
          <TabButton id="typed" label="Type" icon={TypeIcon} />
          <TabButton id="drawn" label="Draw" icon={PenLine} />
          <TabButton id="uploaded" label="Upload" icon={Upload} />
        </div>

        <div className="mt-3">
          {tab === 'typed' && (
            <>
              <div className="flex gap-2">
                {(Object.keys(TYPEFACES) as TypefaceKey[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setTypeface(k)}
                    aria-pressed={typeface === k}
                    className={`min-h-[44px] flex-1 rounded-md border px-2 text-sm ${
                      TYPEFACES[k].className
                    } ${
                      typeface === k
                        ? 'border-tcs-accent-ink bg-[#F4F7FB]'
                        : 'border-tcs-border'
                    }`}
                  >
                    {name.trim() ? name.trim().slice(0, 12) : TYPEFACES[k].label}
                  </button>
                ))}
              </div>
              <div className="mt-3 rounded-md border border-dashed border-tcs-border bg-tcs-bg p-4 text-center">
                <p className={`text-3xl text-tcs-text ${TYPEFACES[typeface].className}`}>
                  {name.trim() || 'Your name'}
                </p>
                <p className="mt-1 text-xs text-tcs-text-muted">Signature preview</p>
              </div>
              <label htmlFor="adopt-initials" className="mt-3 block text-sm font-medium text-tcs-text">
                Initials
              </label>
              <input
                id="adopt-initials"
                value={initialsText}
                onChange={(e) => {
                  setInitialsEdited(true);
                  setInitialsText(e.target.value.toUpperCase().slice(0, 6));
                }}
                className={`mt-1 min-h-[48px] w-28 rounded-md border border-tcs-border px-3 text-center text-xl ${TYPEFACES[typeface].className}`}
              />
            </>
          )}

          {tab === 'drawn' && (
            <>
              <DrawPad onChange={setDrawnSig} ariaLabel="Draw your signature" />
              <p className="mt-3 text-sm font-medium text-tcs-text">
                Initials <span className="font-normal text-tcs-text-muted">(optional)</span>
              </p>
              <DrawPad onChange={setDrawnInitials} ariaLabel="Draw your initials" />
              <p className="mt-1 text-xs text-tcs-text-muted">
                Leave blank and we&rsquo;ll use your signature for initials fields.
              </p>
            </>
          )}

          {tab === 'uploaded' && (
            <>
              <label
                htmlFor="adopt-upload"
                className="flex min-h-[96px] cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-tcs-border bg-tcs-bg p-4 text-sm text-tcs-text-muted"
              >
                <Upload className="h-6 w-6" aria-hidden />
                Choose an image of your signature
              </label>
              <input
                id="adopt-upload"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                }}
              />
              {uploaded && (
                <div className="mt-3 rounded-md border border-dashed border-tcs-border bg-tcs-raised p-3 text-center">
                  <img
                    src={uploaded}
                    alt="Uploaded signature preview"
                    className="mx-auto max-h-24"
                  />
                </div>
              )}
              {uploadError && (
                <p role="alert" className="mt-2 text-sm text-st-danger-fg">{uploadError}</p>
              )}
            </>
          )}
        </div>

        <button
          type="button"
          onClick={adopt}
          disabled={!ready}
          className="mt-5 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-md bg-tcs-accent-ink px-4 text-base font-semibold text-tcs-accent-on disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Check className="h-5 w-5" aria-hidden />
          Adopt and sign
        </button>
        {!ready && (
          <p className="mt-2 text-center text-xs text-tcs-text-muted">
            {!nameOk
              ? 'Enter your full name to continue.'
              : tab === 'drawn'
                ? 'Draw your signature to continue.'
                : tab === 'uploaded'
                  ? 'Choose an image to continue.'
                  : 'Enter your initials to continue.'}
          </p>
        )}
      </div>
    </div>
  );
}
